import React from 'react';
import {useTheme, View, Heading, Authenticator} from '@aws-amplify/ui-react';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import { I18n } from "aws-amplify/utils";

// I18n.setLanguage() only swaps the strings the app renders through I18n.get() - it never touches
// document.documentElement.lang, so screen readers keep announcing content in whatever language was
// set at startup (WCAG 3.1.1). Route every language change through here instead of calling
// I18n.setLanguage() directly so the two stay in sync.
export const setAppLanguage = (lang) => {
    I18n.setLanguage(lang);
    document.documentElement.lang = lang;
}

export const objectIsEmpty = obj => {
    return Object.keys(obj).length === 0;
}

export const candidateForSave = () => {
    let errorElements = document.getElementsByClassName("cdm-field-error");
    return errorElements.length === 0;
};
export const withRouter = (Component) => {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();
        return (
            <Component
                {...props}
                router={{location, navigate, params}}
            />
        );
    }

    return ComponentWithRouterProp;
}

export const formatDateFromMillis = (millis) => {
    if (millis) {
        const dateToFormat = new Date();
        dateToFormat.setTime(millis)
        return dateToFormat.toLocaleDateString("fr-FR");

    }
    return null;
};

export const isEmail = (email) => {
    if (email && email.trim() !== '') {
        return email.match(
            // eslint-disable-next-line
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    }
    return false;
}

export const isFormateur = (authenticatedUserData) => {
    return authenticatedUserData && authenticatedUserData.userType && authenticatedUserData.userType === 'FORMATEUR';
}

export const ticketSelect = (id) => {
    const selectedCard = document.getElementById(id).firstChild;
    return !selectedCard.classList.contains("cdm-selected-card");
}

export const ticketHSelect = (id) => {
    const selectedCard = document.getElementById(id).childNodes[1];
    return !selectedCard.classList.contains("cdm-selected-card");
}

export const overLap = (sesHashA, sesHashB) => {
    return sesHashA.jour === sesHashB.jour && (testInterval(sesHashA.sHour, sesHashB) || testInterval(sesHashB.sHour, sesHashA) || testInterval(sesHashA.eHour, sesHashB) || testInterval(sesHashB.eHour, sesHashA.sHour));
}

const testInterval = (hourToTest, againstHash) => {
    return (hourToTest >= againstHash.sHour && hourToTest <= againstHash.eHour);
}

export const flags = {FR: "assets/layout/images/icon-flag-fr.png", FRFLAG: "assets/layout/images/icon-flag-fr.png", DE: "assets/layout/images/icon-flag-de.png", DEFLAG: "assets/layout/images/flag-de.png"};

export const cities = {esch: "assets/layout/images/esch.png", ette: "assets/layout/images/ette.png", luxe: "assets/layout/images/luxe.png", lu: "assets/layout/images/lu.png"};

export const LINKED_MODULES = ['MOD_A', 'MOD_D', 'MOD_E'];

export const MODULE_PRATIQUE = 'MOD_I';

// PrimeReact's Dropdown always renders a hidden native <select> mirror (for native form
// fallback/autofill) alongside the visible combobox. In v9 that mirror carried
// aria-hidden="true"; PrimeReact v10 dropped it, leaving an unlabelled control exposed to
// screen readers (WCAG 4.1.2). Spread this as the Dropdown's `pt` prop to restore it.
export const DROPDOWN_HIDDEN_SELECT_PT = {select: {'aria-hidden': true}};

const PhoneEuropeRegExp = () => {
    // eu phones map https://en.wikipedia.org/wiki/Telephone_numbers_in_Europe
    return new RegExp(
        /(\+|00)(297|93|244|1264|358|355|376|971|54|374|1684|1268|61|43|994|257|32|229|226|880|359|973|1242|387|590|375|501|1441|591|55|1246|673|975|267|236|1|61|41|56|86|225|237|243|242|682|57|269|238|506|53|5999|61|1345|357|420|49|253|1767|45|1809|1829|1849|213|593|20|291|212|34|372|251|358|679|500|33|298|691|241|44|995|44|233|350|224|590|220|245|240|30|1473|299|502|594|1671|592|852|504|385|509|36|62|44|91|246|353|98|964|354|972|39|1876|44|962|81|76|77|254|996|855|686|1869|82|383|965|856|961|231|218|1758|423|94|266|370|352|371|853|590|212|377|373|261|960|52|692|389|223|356|95|382|976|1670|258|222|1664|596|230|265|60|262|264|687|227|672|234|505|683|31|47|977|674|64|968|92|507|64|51|63|680|675|48|1787|1939|850|351|595|970|689|974|262|40|7|250|966|249|221|65|500|4779|677|232|503|378|252|508|381|211|239|597|421|386|46|268|1721|248|963|1649|235|228|66|992|690|993|670|676|1868|216|90|688|886|255|256|380|598|1|998|3906698|379|1784|58|1284|1340|84|678|681|685|967|27|260|263)(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{4,20}$/);
};

// Builds the "Matière / Thématique" text shown in ScheduleEvent's detail popup, for reuse
// wherever a schedule entry needs the same information outside the calendar UI (e.g. ICS export).
export const buildEventDescription = (entry) => {
    const lang = I18n.get('language').toUpperCase();
    const matiereLabel = entry?.nomsMatiere ? (entry.nomsMatiere[lang] ?? entry.nomsMatiere.FR) : null;
    const thematiqueLabel = entry?.nomsThematique ? (entry.nomsThematique[lang] ?? entry.nomsThematique.FR) : null;

    return [
        matiereLabel && `${I18n.get("calendar.matiere")}: ${matiereLabel}`,
        thematiqueLabel && `${I18n.get("calendar.thematique")}: ${thematiqueLabel}`
    ].filter(Boolean).join("\n");
}

export  const sanitizeFileName = (fileName) => {
    return fileName.replace(/[&/\\#,+()$~%ç|§¬°§'":*?<>{}]/g, '').replace(/[éèêë]/g, 'e').replace(/[ÉÈÊ]/g, 'E').replace(/[àäâ]/g, 'a').replace(/[ÀÄÂ]/g, 'A').replace(/[ç]/g, 'c').replace(/[Ç]/g, 'C').replace(/[üù]/g, 'u').replace(/[ÜÙ]/g, 'U').replace(/[ ]/g, '_');
}

export const isPhoneNumber = (phoneNr) => {
    return PhoneEuropeRegExp().test(phoneNr);
}

export const components = {
    Header() {
        const {tokens} = useTheme();

        return (
            <View textAlign="center" padding={tokens.space.large}>
                <div className='cdm-logo'
                />
            </View>
        );
    },

    SignIn: {
        Header() {
            const {tokens} = useTheme();
            return (
                <Heading
                    padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                    level={3}
                >
                    {I18n.get("Welcome")}
                </Heading>
            );
        },
    },
    ForceNewPassword: {
        FormFields() {
            return (
                <>
                    {I18n.get("passwordpolicy")}
                    <Authenticator.ForceNewPassword.FormFields/>
                </>
            );
        },
    },
};

export const formFields = {
    setupTOTP: {
        QR: {
            totpIssuer: 'CdM myHandwierk',
        },
    },
}
